import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import ChatBox from "@/components/ChatBox";
import BadgePills from "@/components/BadgePills";
import { MapPin, CheckCircle2, Clock, Car, Phone, User, PhoneCall, AlertTriangle, CreditCard, Banknote, Loader2, X } from "lucide-react";

const STATUS_MAP = {
  pending:     { label: "En attente d'un chauffeur",     color: "bg-[hsl(var(--warning))]", dot: 1 },
  accepted:    { label: "Chauffeur en route",            color: "bg-primary",                 dot: 2 },
  picked_up:   { label: "Client pris en charge",         color: "bg-accent",                  dot: 3 },
  in_progress: { label: "Trajet en cours",               color: "bg-accent",                  dot: 4 },
  completed:   { label: "Arrivé à destination",          color: "bg-[hsl(var(--success))]",   dot: 5 },
  cancelled:   { label: "Course annulée",                color: "bg-destructive",             dot: 0 },
};

const STEPS = [
  { k: 1, label: "Réservation confirmée" },
  { k: 2, label: "Chauffeur attribué" },
  { k: 3, label: "Client pris en charge" },
  { k: 4, label: "Trajet en cours" },
  { k: 5, label: "Arrivé à destination" },
];

const REPORT_REASONS = [
  { value: "chauffeur_retard", label: "Chauffeur en retard" },
  { value: "chauffeur_absent", label: "Chauffeur introuvable" },
  { value: "mauvais_comportement", label: "Mauvais comportement" },
  { value: "vehicule_different", label: "Véhicule ne correspond pas" },
  { value: "autre", label: "Autre" },
];

export function computeEtaRemaining(etaMinutes, etaSetAt) {
  if (!etaMinutes || !etaSetAt) return null;
  const startMs = new Date(etaSetAt).getTime();
  const elapsedMin = (Date.now() - startMs) / 60000;
  return Math.ceil(etaMinutes - elapsedMin);
}

export default function BookingTimeline({ token, compact = false, showTimeline = true, showCancel = false }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [tick, setTick] = useState(0);

  // Problem report dialog
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("chauffeur_retard");
  const [reportMessage, setReportMessage] = useState("");
  const [reportSending, setReportSending] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);

  // ---- Cancel action ----
  const cancelBooking = async () => {
    if (cancelLoading) return;
    setCancelLoading(true);
    try {
      await api.post(`/track/${token}/cancel`);
      toast.success("Course annulée.");
      setCancelConfirm(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setCancelLoading(false);
    }
  };

  // ---- Payment actions ----
  const payWithCard = async () => {
    setPayLoading(true);
    try {
      const { data: d } = await api.post(`/track/${token}/checkout-session`, { origin_url: window.location.origin });
      window.location.href = d.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setPayLoading(false);
    }
  };

  const chooseCash = async () => {
    setPayLoading(true);
    try {
      await api.post(`/track/${token}/cash-intent`);
      toast.success("Paiement en espèces sélectionné — remettez l'argent au chauffeur.");
      setTick((n) => n + 1);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setPayLoading(false); }
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/track/${token}`);
      setData(data);
      setErr(null);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  useEffect(() => {
    // If the client returns from Stripe Checkout with ?paid=1, immediately
    // call /track/{token}/sync-payment so the booking is marked paid (webhook
    // fallback), then poll aggressively until the status flips.
    const params = new URLSearchParams(window.location.search);
    if (params.get("paid") === "1") {
      toast.success("Merci ! Paiement en cours de confirmation…");
      window.history.replaceState(null, "", window.location.pathname);
      // Force backend sync first
      api.post(`/track/${token}/sync-payment`).catch(() => {});
      let tries = 0;
      const fastPoll = setInterval(async () => {
        tries += 1;
        try { await api.post(`/track/${token}/sync-payment`); } catch { /* noop */ }
        setTick((t) => t + 1);
        if (tries > 10) clearInterval(fastPoll);
      }, 3000);
      return () => clearInterval(fastPoll);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!token) return;
    load();
    const pollT = setInterval(load, 5000);
    const tickT = setInterval(() => setTick((t) => t + 1), 30000);
    return () => { clearInterval(pollT); clearInterval(tickT); };
    // eslint-disable-next-line
  }, [token]);

  const mockedRemaining = useMemo(() => data ? computeEtaRemaining(data.eta_minutes, data.eta_set_at) : null, [data, tick]);
  const remaining = mockedRemaining;

  const submitReport = async () => {
    setReportSending(true);
    try {
      const label = REPORT_REASONS.find((r) => r.value === reportReason)?.label || reportReason;
      await api.post(`/track/${token}/report`, { reason: label, message: reportMessage });
      toast.success("Signalement envoyé — notre équipe a été alertée.");
      setReportOpen(false);
      setReportMessage("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setReportSending(false); }
  };

  if (err) return <div className="rounded-xl border border-border bg-card p-6 text-sm text-destructive">{err}</div>;
  if (!data) {
    return (
      <div className="grid place-items-center rounded-xl border border-border bg-card p-10">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary/20 border-t-primary" />
      </div>
    );
  }

  const cfg = STATUS_MAP[data.status] || STATUS_MAP.pending;

  // Once the payment is confirmed, we simplify the view to a single, clean
  // confirmation card — everything else (map, ETA, chat, contact buttons)
  // disappears to signal that nothing is left to do.
  if (data.payment_status === "paid") {
    return (
      <div className="mx-auto max-w-xl px-4 py-12" data-testid="client-paid-view">
        <div className="rounded-3xl border-2 border-[hsl(var(--success))]/40 bg-gradient-to-br from-[hsl(var(--success))]/10 via-card to-card p-8 text-center shadow-sm sm:p-12">
          <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <h1 className="mt-5 font-[Outfit] text-3xl font-normal tracking-tight sm:text-4xl">Paiement confirmé</h1>
          <p className="mt-3 text-lg font-medium text-[hsl(var(--success))]">{data.price} €</p>
          <p className="mt-2 text-sm text-muted-foreground">
            {data.payment_method === "cash" ? "Réglé en espèces auprès du chauffeur." : "Réglé par carte."}
          </p>
          <div className="mt-6 rounded-xl border border-border bg-secondary/40 p-4 text-left text-sm">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Détails de la course</p>
            <p className="mt-2"><strong>{data.departure_address}</strong></p>
            <p className="text-muted-foreground">→ {data.arrival_address}</p>
            {data.driver_name && <p className="mt-2">Chauffeur : <strong>{data.driver_name}</strong></p>}
          </div>
          <p className="mt-6 text-xs text-muted-foreground">Merci d'avoir choisi Price — bonne route 🚗</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="booking-timeline">
      <div className="flex flex-wrap items-center gap-3">
        <Badge className={`${cfg.color} text-white`} data-testid="timeline-status">{cfg.label}</Badge>
        <span className="text-xs text-muted-foreground">Mis à jour en continu</span>
      </div>

      {/* ETA hero — big countdown when driver is en route */}
      {["accepted", "picked_up", "in_progress"].includes(data.status) && remaining != null && remaining > 0 && (
        <div className="flex items-end justify-between gap-3 rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/8 to-card p-4" data-testid="tracking-eta-block">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-primary">
              {data.status === "accepted" ? "Chauffeur en approche" : data.status === "picked_up" ? "Chauffeur arrivé" : "ETA destination"}
            </p>
            <p className="mt-1 flex items-baseline gap-1 leading-none">
              <span className="font-[Outfit] text-5xl font-semibold tracking-tight text-primary sm:text-6xl" data-testid="tracking-eta-min">{remaining}</span>
              <span className="font-[Outfit] text-xl font-medium text-primary/80">min</span>
            </p>
          </div>
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-primary" />
          </span>
        </div>
      )}

      {data.driver_name && (data.driver_profile_photo || data.driver_license_plate) && (
        <DriverCard
          name={data.driver_name}
          phone={data.driver_phone}
          profilePhoto={data.driver_profile_photo}
          vehiclePhoto={data.driver_vehicle_photo}
          licensePlate={data.driver_license_plate}
          vehicleBrandModel={data.driver_vehicle_brand_model}
          badges={data.driver_badges}
        />
      )}

      {/* Payment card — client pays directly from the tracking page */}
      {["accepted", "picked_up", "in_progress"].includes(data.status) && (
        data.payment_status === "paid" ? (
          <div className="flex items-center gap-3 rounded-2xl border border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10 px-4 py-3 text-[hsl(var(--success))]" data-testid="client-payment-confirmed">
            <CheckCircle2 className="h-5 w-5" />
            <div>
              <p className="font-[Outfit] text-base font-medium">Paiement confirmé — {data.price} €</p>
              <p className="text-xs opacity-80">{data.payment_method === "cash" ? "Réglé en espèces auprès du chauffeur" : "Réglé par carte"}</p>
            </div>
          </div>
        ) : data.payment_method === "cash" ? (
          <div className="flex items-center gap-3 rounded-2xl border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 px-4 py-3" data-testid="client-payment-cash-chosen">
            <Banknote className="h-5 w-5 text-[hsl(var(--warning))]" />
            <div>
              <p className="font-[Outfit] text-base font-medium">Paiement en espèces · {data.price} €</p>
              <p className="text-xs text-muted-foreground">Réglez l'argent au chauffeur. La confirmation sera faite par le chauffeur une fois reçue.</p>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-border bg-card p-5" data-testid="client-payment-picker">
            <p className="font-[Outfit] text-lg font-medium">Régler la course</p>
            <p className="mt-1 text-sm text-muted-foreground">Montant : <strong>{data.price} €</strong></p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <Button
                onClick={payWithCard}
                disabled={payLoading}
                className="h-14 rounded-xl bg-primary hover:bg-primary/90"
                data-testid="client-pay-card"
              >
                {payLoading ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <CreditCard className="mr-2 h-5 w-5" />}
                Carte / Apple Pay / Google Pay
              </Button>
              <Button
                onClick={chooseCash}
                disabled={payLoading}
                variant="outline"
                className="h-14 rounded-xl"
                data-testid="client-pay-cash"
              >
                <Banknote className="mr-2 h-5 w-5" /> Payer en espèces
              </Button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">Paiement en ligne sécurisé. 100 % du montant va au chauffeur une fois la commission plateforme prélevée.</p>
          </div>
        )
      )}

      {/* Primary contact actions — visible as soon as a driver is assigned */}
      {data.driver_phone && ["accepted", "picked_up", "in_progress"].includes(data.status) && (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="client-actions">
          <Button
            asChild
            size="lg"
            className="h-14 rounded-xl bg-[hsl(var(--success))] text-white shadow-sm hover:bg-[hsl(var(--success))]/90"
            data-testid="call-driver-btn"
          >
            <a href={`tel:${data.driver_phone}`}>
              <PhoneCall className="mr-2 h-5 w-5" />
              Appeler le chauffeur
            </a>
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="h-14 rounded-xl border-destructive/30 text-destructive hover:bg-destructive/5 hover:text-destructive"
            onClick={() => setReportOpen(true)}
            data-testid="report-btn"
          >
            <AlertTriangle className="mr-2 h-5 w-5" />
            Signaler un problème
          </Button>
        </div>
      )}

      {/* Cancel CTA — only on the public tracking view (showCancel prop),
          and only while the trip hasn't started yet. */}
      {showCancel && ["pending", "accepted"].includes(data.status) && (
        <>
          {!cancelConfirm ? (
            <button
              type="button"
              onClick={() => setCancelConfirm(true)}
              disabled={cancelLoading}
              className="flex h-11 w-full items-center justify-center gap-2 rounded-2xl border border-destructive/30 bg-destructive/5 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10 disabled:opacity-50"
              data-testid="tracking-cancel-btn"
            >
              <X className="h-4 w-4" />
              Annuler la course
            </button>
          ) : (
            <div className="rounded-2xl border border-destructive/40 bg-destructive/5 p-4" data-testid="tracking-cancel-confirm">
              <p className="text-sm font-medium text-foreground">Confirmer l'annulation ?</p>
              <p className="mt-1 text-xs text-muted-foreground">Le chauffeur sera immédiatement notifié. Aucun frais ne sera retenu.</p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <Button variant="outline" size="sm" onClick={() => setCancelConfirm(false)} disabled={cancelLoading} data-testid="tracking-cancel-keep">
                  Conserver
                </Button>
                <Button
                  onClick={cancelBooking}
                  disabled={cancelLoading}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  data-testid="tracking-cancel-confirm-btn"
                >
                  {cancelLoading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <X className="mr-1.5 h-4 w-4" />}
                  Oui, annuler
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Realtime chat with the driver — always open once the driver accepts */}
      {["accepted", "picked_up", "in_progress"].includes(data.status) && (
        <div data-testid="client-chat-wrap">
          <p className="mb-2 font-[Outfit] text-lg font-medium">Messagerie avec le chauffeur</p>
          <ChatBox token={token} author="client" label={data.client_name || "Client"} />
        </div>
      )}

      {/* Timeline — hidden on the client tracking view (TrackingPage passes
          `showTimeline={false}`). Admin / hotel consoles keep it for ops
          visibility. */}
      {showTimeline && (
        <ol className="space-y-0">
          {STEPS.map((step, i) => {
            const done = cfg.dot >= step.k;
            const current = cfg.dot === step.k;
            return (
              <li key={step.k} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className={`grid h-8 w-8 place-items-center rounded-full border-2 transition-colors ${done ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground"} ${current ? "ring-2 ring-primary/20 ring-offset-2 ring-offset-background" : ""}`}>
                    {done ? <CheckCircle2 className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                  </div>
                  {i < STEPS.length - 1 && <div className={`h-10 w-[2px] ${done && cfg.dot > step.k ? "bg-primary" : "bg-border"}`} />}
                </div>
                <div className="pt-1 pb-8">
                  <p className={`text-sm ${done ? "font-medium" : "text-muted-foreground"}`}>{step.label}</p>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {!compact && (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <Block icon={<MapPin className="h-4 w-4" />} label="Départ" value={data.departure_address} />
            <Block icon={<MapPin className="h-4 w-4" />} label="Arrivée" value={data.arrival_address} />
            <Block icon={<User className="h-4 w-4" />} label="Passagers" value={`${data.passenger_count} pers · ${data.luggage_count} bagages`} />
            <Block icon={<Car className="h-4 w-4" />} label="Chauffeur" value={data.driver_name || "En recherche..."} extra={data.driver_phone} />
          </div>

          <div className="rounded-xl border border-border bg-secondary/40 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Tarif</span>
              <span className="text-xl font-[Outfit]">{data.price} €</span>
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              Paiement : <strong>{data.payment_status === "paid" ? "Réglé" : "À régler"}</strong>
            </div>
          </div>
        </>
      )}

      {/* Report dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle>Signaler un problème</DialogTitle>
            <DialogDescription>
              Notre équipe (contact@pricecovoit.fr) est notifiée immédiatement et vous recontactera si nécessaire.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Motif</Label>
              <RadioGroup value={reportReason} onValueChange={setReportReason} className="gap-2">
                {REPORT_REASONS.map((r) => (
                  <label key={r.value} className="flex cursor-pointer items-center gap-2.5 rounded-lg border border-border bg-card px-3 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                    <RadioGroupItem value={r.value} data-testid={`report-reason-${r.value}`} />
                    <span>{r.label}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>
            <div className="space-y-2">
              <Label>Message (facultatif)</Label>
              <Textarea value={reportMessage} onChange={(e) => setReportMessage(e.target.value)} placeholder="Décrivez rapidement..." rows={3} data-testid="report-message" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReportOpen(false)}>Annuler</Button>
            <Button onClick={submitReport} disabled={reportSending} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="report-submit">
              {reportSending ? "Envoi..." : "Envoyer le signalement"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Block({ icon, label, value, extra }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground">{icon} {label}</div>
      <p className="mt-1.5 text-sm">{value}</p>
      {extra && <p className="text-xs text-muted-foreground"><Phone className="mr-1 inline h-3 w-3" /> {extra}</p>}
    </div>
  );
}

function DriverCard({ name, phone, profilePhoto, vehiclePhoto, licensePlate, vehicleBrandModel, badges }) {
  const profileSrc = profilePhoto ? `data:${profilePhoto.mime_type};base64,${profilePhoto.data}` : null;
  const vehicleSrc = vehiclePhoto ? `data:${vehiclePhoto.mime_type};base64,${vehiclePhoto.data}` : null;
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm" data-testid="driver-card">
      <div className="flex flex-col gap-5 p-5 sm:flex-row sm:items-center">
        {profileSrc ? (
          <img src={profileSrc} alt={name} className="h-20 w-20 shrink-0 rounded-full border-2 border-primary/10 object-cover" data-testid="driver-profile-photo" />
        ) : (
          <div className="grid h-20 w-20 shrink-0 place-items-center rounded-full bg-secondary text-2xl font-[Outfit]">
            {(name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Votre chauffeur</p>
          <p className="mt-1 font-[Outfit] text-xl" data-testid="driver-card-name">{name}</p>
          {phone && (
            <a href={`tel:${phone}`} className="mt-0.5 inline-flex items-center gap-1 text-sm text-primary underline-offset-4 hover:underline">
              <Phone className="h-3 w-3" /> {phone}
            </a>
          )}
          {vehicleBrandModel && <p className="mt-1 text-sm text-muted-foreground">{vehicleBrandModel}</p>}
          {licensePlate && <LicensePlate value={licensePlate} />}
          {badges && badges.length > 0 && (
            <div className="mt-2">
              <BadgePills badges={badges} size="sm" />
            </div>
          )}
        </div>
        {vehicleSrc && (
          <img src={vehicleSrc} alt="Véhicule" className="h-24 w-full shrink-0 rounded-xl object-cover sm:h-24 sm:w-40" data-testid="driver-vehicle-photo" />
        )}
      </div>
    </div>
  );
}

function LicensePlate({ value }) {
  return (
    <div className="mt-2 inline-flex overflow-hidden rounded-md border-2 border-neutral-900 bg-white font-mono text-base font-bold tracking-[0.18em] text-neutral-900 shadow-sm" data-testid="driver-license-plate">
      <span className="flex min-w-[22px] items-end justify-center bg-[#003399] px-1 pb-1 pt-3 text-[9px] text-yellow-300">F</span>
      <span className="px-3 py-1.5">{value}</span>
    </div>
  );
}
